/**
 * Stytch Session Management Hooks
 *
 * These hooks use Stytch's official useStytchMemberSession hook
 * and provide convenient wrappers for common session operations.
 */
import React, { useCallback } from 'react';
import { useStytchMemberSession, useStytchOrganization as useStytchOrgHook } from '@stytch/nextjs/b2b';
import { getStytchClient } from './../client/headless-client';
/**
 * Hook to get current Stytch session using official useStytchMemberSession
 * Returns the cached session object or null if not logged in
 */
export function useStytchSession() {
    const { session } = useStytchMemberSession();
    return session;
}
/**
 * Hook to get session info using official useStytchMemberSession
 * Returns both the session object and a fromCache boolean
 */
export function useStytchSessionInfo() {
    const { session, fromCache } = useStytchMemberSession();
    return session ? { session: session, fromCache } : null;
}
/**
 * Hook to check if user is authenticated using Stytch session
 */
export function useStytchIsAuthenticated() {
    const { session } = useStytchMemberSession();
    return !!session;
}
/**
 * Hook to get current member from Stytch session
 */
export function useStytchMember() {
    const { session } = useStytchMemberSession();
    const extendedSession = session;
    return extendedSession?.member ?? null;
}
/**
 * Hook to get current organization from Stytch session
 * @deprecated Use useOrganization() instead for full organization data
 */
export function useStytchOrganization() {
    const { session } = useStytchMemberSession();
    const extendedSession = session;
    return extendedSession?.organization ?? null;
}
/**
 * Hook to get full organization data from Stytch
 * Returns organization with all properties including sso_default_connection_id
 */
export function useOrganization() {
    const { organization, isInitialized, fromCache } = useStytchOrgHook();
    // Function to refresh organization data by re-authenticating session
    const refreshOrganization = useCallback(async () => {
        const stytch = getStytchClient();
        if (!stytch) {
            return { success: false, error: 'Stytch client not ready' };
        }
        try {
            // Re-authenticate session to refresh all cached data including organization
            await stytch.session.authenticate();
            return { success: true };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to refresh organization',
            };
        }
    }, []);
    const org = organization;
    return {
        organization,
        isInitialized,
        fromCache,
        // Convenience accessors
        defaultSSOConnectionId: org?.sso_default_connection_id ?? null,
        organizationId: org?.organization_id ?? null,
        organizationName: org?.organization_name ?? null,
        // SSO JIT Provisioning settings (affects which connections appear in discoverConnections)
        ssoJitProvisioning: org?.sso_jit_provisioning ?? null,
        ssoJitProvisioningAllowedConnections: org?.sso_jit_provisioning_allowed_connections ?? null,
        ssoActiveConnections: org?.sso_active_connections ?? null,
        emailAllowedDomains: org?.email_allowed_domains ?? null,
        // Refresh function
        refreshOrganization,
    };
}
/**
 * Hook to get session JWT from Stytch session
 */
export function useStytchSessionJWT() {
    const { session } = useStytchMemberSession();
    const extendedSession = session;
    return extendedSession?.session_jwt ?? null;
}
/**
 * Hook to get session token from Stytch session
 */
export function useStytchSessionToken() {
    const { session } = useStytchMemberSession();
    const extendedSession = session;
    return extendedSession?.session_token ?? null;
}
/**
 * Hook to listen for session changes with custom callback
 * Uses the official useStytchMemberSession hook's onChange
 */
export function useStytchSessionChange(callback) {
    const { session } = useStytchMemberSession();
    // Call the callback whenever session changes
    React.useEffect(() => {
        callback(session);
    }, [session, callback]);
}
/**
 * Hook to manually refresh session
 */
export function useStytchRefreshSession() {
    const refreshSession = useCallback(async () => {
        const stytch = getStytchClient();
        if (!stytch) {
            return {
                success: false,
                error: 'Stytch client not ready',
            };
        }
        try {
            await stytch.session.authenticate();
            return { success: true };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to refresh session',
            };
        }
    }, []);
    return refreshSession;
}
/**
 * Hook to revoke session (logout)
 */
export function useStytchRevokeSession() {
    const revokeSession = useCallback(async () => {
        const stytch = getStytchClient();
        if (!stytch) {
            return {
                success: false,
                error: 'Stytch client not ready',
            };
        }
        try {
            await stytch.session.revoke();
            return { success: true };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to revoke session',
            };
        }
    }, []);
    return revokeSession;
}
/**
 * Hook to get all session-related functions and state
 * Uses the official useStytchMemberSession hook as the foundation
 */
export function useStytchSessionManagement() {
    const { session, fromCache } = useStytchMemberSession();
    const refreshSession = useStytchRefreshSession();
    const revokeSession = useStytchRevokeSession();
    const extendedSession = session;
    return {
        // Session state from official hook
        session: extendedSession,
        sessionInfo: extendedSession ? { session: extendedSession, fromCache } : null,
        isAuthenticated: !!session,
        member: extendedSession?.member ?? null,
        organization: extendedSession?.organization ?? null,
        sessionJWT: extendedSession?.session_jwt ?? null,
        sessionToken: extendedSession?.session_token ?? null,
        // Session actions
        refreshSession,
        revokeSession,
        // Utility functions
        getSessionSync: () => extendedSession,
        getSessionInfo: () => (extendedSession ? { session: extendedSession, fromCache } : null),
    };
}
